<template>
  <div>
    <div v-show="loading" class="text-center">
      <v-progress-circular style="width: 64px;height:64px;"
                           indeterminate size="64"
                           color="primary"
      />
    </div>

    <div v-show="!loading">
      <v-card>
        <p v-if="itemCount !== 0" class="ma-4">
          {{ $t('archives.ArchivesList.itemCount', itemCount) }}
        </p>

        <v-data-table-server v-model="selectedItems"
                             :items="items"
                             :items-length="itemCount"
                             must-sort
                             :headers="columns"
                             :sort-by="sort"
                             :show-select="true"
                             :return-object="true"
                             :value-comparator="comparatorFunction"
                             @update:sort-by="onSortChange"
        >
          <template #item.docName="{ item }">
            <AppTooltip location="bottom" max-width="300">
              <template v-slot:activator="{ props }">
                <div class="d-flex align-center" v-bind="props">
                  <ClosdFildersIcon class="mr-2"
                                    :document="item"
                                    :signedFile="item.isSigned == true"
                  />
                  <span class="text-truncated">{{ item.docName }}</span>
                </div>
              </template>
              <span>{{ item.docName }}</span>
            </AppTooltip>
          </template>

          <template #item.archiveDate="{ item }">
            <DateDisplay :value="item.archiveDate" />
          </template>

          <template #item.archiverName="{ item }">
            <ArchivesListArchiverName :name="item.archiverName" />
          </template>

          <template #item.signedDocument="{ item }">
            <BooleanDisplay :value="item.isSigned == true" />
          </template>

          <template #item.signedDate="{ item }">
            <DateDisplay :value="item.signedDate" />
          </template>

          <template #item._options="{item, index}">
            <div style="min-width: 75px">
              <AppButton v-if="isArchivingAdmin || item.canDownload"
                         icon
                         density="comfortable"
                         @click="downloadFile(item)"
              >
                <font-awesome-icon :icon="['fas', 'download']" class="text-grey-500" />
              </AppButton>
              <AppMenu :menu="getMenuForItem(item)"
                       min-width="250px"
                       @update:return-value="indexMenuOpen = null"
              >
                <template #activator="{ props }">
                  <AppButton icon
                             density="comfortable"
                             v-bind="props"
                             @click="toggleMenu(index)"
                  >
                    <font-awesome-icon :icon="['fas', 'ellipsis-v']" class="text-grey-500" />
                  </AppButton>
                </template>
              </AppMenu>
            </div>
          </template>

          <template #bottom>
            <div class="d-flex justify-center ma-4">
              <AppPagination :current-page="page"
                             :page-size="ARCHIVES_LIST_PAGE_SIZE"
                             :total="itemCount"
                             hide-page-size
                             @update:current-page="onPageChange"
              />
            </div>
          </template>
        </v-data-table-server>
      </v-card>
    </div>

    <AppDownloadFile ref="appDownloadFileRef" :link="downloadLink" />

    <ArchivesListDetailsDialog v-if="currentItemDetailsOpen"
                               :archive="currentItemDetails"
                               :fields="modalFields"
                               @action="handleDetailsModalAction"
                               @close="currentItemDetailsOpen = false"
    />

    <ArchivesListShareDialog v-if="shareDialogOpen"
                             :data="currentShareItems"
                             @close="shareDialogOpen = false"
    />

    <ArchivesListSendByEmailDialog v-if="archiveToBeSendByEmail"
                                   :archive-id="archiveToBeSendByEmail.id"
                                   @close="archiveToBeSendByEmail = null"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import BooleanDisplay from '@/common/BooleanDisplay.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { ArchivesActionTypes } from '@/store/modules/archives/action_types'

import ArchivesListArchiverName from './ArchivesListArchiverName.vue'
import ArchivesListDetailsDialog from './dialogs/ArchivesListDetailsDialog.vue'
import ArchivesListSendByEmailDialog from './dialogs/ArchivesListSendByEmailDialog.vue'
import ArchivesListShareDialog from './dialogs/ArchivesListShareDialog.vue'

const ARCHIVES_LIST_PAGE_SIZE = 25

export default {
  name: 'ArchivesList',
  components: {
    AppButton,
    AppDownloadFile,
    AppMenu,
    AppPagination,
    AppTooltip,
    ArchivesListArchiverName,
    ArchivesListDetailsDialog,
    ArchivesListSendByEmailDialog,
    ArchivesListShareDialog,
    BooleanDisplay,
    ClosdFildersIcon,
    DateDisplay,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      required: true,
    },
    itemCount: {
      type: Number,
      required: true,
    },
    additionalFields: {
      type: Array,
      default: () => [],
    },
    sort: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['page-change', 'sort-change'],
  data () {
    return {
      ARCHIVES_LIST_PAGE_SIZE,
      indexMenuOpen: null,
      currentItemDetails: null,
      currentItemDetailsOpen: false,
      currentShareItems: [],
      shareDialogOpen: false,
      downloadLink: '',
      archiveToBeSendByEmail: null,
    }
  },
  computed: {
    ...mapState('archives', ['selectedDocuments']),
    ...mapGetters('user', ['isArchivingAdmin']),
    selectedItems: {
      get () {
        return this.selectedDocuments
      },
      set (val) {
        this.SELECT_DOCUMENTS(val)
      },
    },
    computedAdditionalFields () {
      return (this.additionalFields ?? []).map(metadata => ({
        value: metadata.field,
        title: metadata[this.$i18n.locale],
        sortable: true,
      }))
    },
    columns () {
      const fields = [
        {
          value: 'docName',
          title: this.$t('archives.ArchivesList.nameField'),
          sortable: true,
        },
        {
          value: 'cmRef',
          title: this.$t('archives.ArchivesList.folderRefField'),
          sortable: true,
        },
        {
          value: 'archiveDate',
          title: this.$t('archives.ArchivesList.archiveDateField'),
          sortable: true,
        },
        {
          value: 'archiverName',
          title: this.$t('archives.ArchivesList.archiverNameField'),
          sortable: true,
        },
        {
          value: 'signedDocument',
          title: this.$t('archives.ArchivesList.signedDocumentField'),
        },
        {
          value: 'signedDate',
          title: this.$t('archives.ArchivesList.signDateField'),
          sortable: true,
        },
      ]

      fields.push(...this.computedAdditionalFields)

      fields.push(
        {
          value: '_options',
          title: this.$t('archives.ArchivesList.optionsField'),
          sortable: false,
        },
      )

      return fields
    },
    modalFields () {
      return [
        {
          value: 'cmRef',
          title: this.$t('archives.ArchivesList.folderRefField'),
        },
        {
          value: 'signersNames',
          title: this.$t('archives.ArchivesList.signersNamesField'),
        },
        {
          value: 'archiverName',
          title: this.$t('archives.ArchivesList.archiverNameField'),
        },
        {
          value: 'signedDate',
          title: this.$t('archives.ArchivesList.signDateField'),
        },
        {
          value: 'docExt',
          title: this.$t('archives.ArchivesList.documentFormatField'),
        },
        {
          value: 'docSize',
          title: this.$t('archives.ArchivesList.documentSizeField'),
        },
        {
          value: 'signedDocument',
          title: this.$t('archives.ArchivesList.signedDocumentField'),
        },
        {
          value: 'archiveDate',
          title: this.$t('archives.ArchivesList.archiveDateField'),
        },
        {
          value: 'archiveEndDate',
          title: this.$t('archives.ArchivesList.archiveEndDateField'),
        },
        ...this.computedAdditionalFields,
      ]
    },
  },
  methods: {
    ...mapActions('archives', [ArchivesActionTypes.SELECT_DOCUMENTS]),
    async downloadCertificate (item) {
      this.downloadLink = `${axios.defaults.baseURL}/downloads/downloadArchiveCert/${item.id}`
      await this.$nextTick()
      this.$refs.appDownloadFileRef.submitForm()
    },
    async downloadFile(item) {
      this.downloadLink = `${axios.defaults.baseURL}/downloads/downloadArchiveDoc/${item.id}`
      await this.$nextTick()
      this.$refs.appDownloadFileRef.submitForm()
    },
    getMenuForItem (item) {
      const menuItems = [
        {
          title: this.$t('archives.ArchivesList.propertiesOption'),
          action: this.showCurrentDetails,
        },
      ]

      if (this.isArchivingAdmin) {
        menuItems.unshift(
          {
            title: this.$t('archives.ArchivesList.shareOption'),
            action: () => this.shareCurrent(item),
          },
        )
      }

      if (item.hasCertificate) {
        menuItems.push({
          title: this.$t('archives.ArchivesList.certificateOption'),
          action: () => this.downloadCertificate(item),
        })
      }

      menuItems.push({
        title: this.$t('archives.ArchivesList.sendByEmail'),
        action: () => {
          this.archiveToBeSendByEmail = item
        },
      })

      return menuItems
    },
    handleDetailsModalAction (actionName) {
      switch (actionName) {
        case 'download':
          this.downloadFile(this.currentItemDetails)
          break
        case 'share':
          this.shareCurrent(this.currentItemDetails)
          break
        default:
          break
      }
    },
    shareCurrent (item) {
      this.currentShareItems = [item]
      this.shareDialogOpen = true
    },
    showCurrentDetails () {
      this.currentItemDetails = this.items[this.indexMenuOpen]
      this.currentItemDetailsOpen = true
    },
    toggleMenu (rowIndex) {
      if (this.indexMenuOpen !== rowIndex) {
        this.indexMenuOpen = rowIndex
      } else {
        // Reset
        this.indexMenuOpen = null
      }
    },
    onPageChange (page) {
      this.$emit('page-change', page)
    },
    onSortChange (sort) {
      this.$emit('sort-change', sort)
    },
    comparatorFunction (a, b) {
      return a.id === b.id
    },
  },
}
</script>
